/**
 * Language detection for the broadcast panel.
 *
 * Priority:
 *  1. Telegram's currently selected UI language (via selectLanguageCode from global state).
 *  2. Browser language (navigator.language); anything starting with 'zh' → 'zh', else 'en'.
 *  3. 'en' as ultimate fallback.
 */

import { getGlobal } from '../../global';

import { selectLanguageCode } from '../../global/selectors/settings';

export type BroadcastLang = 'zh' | 'en';

const isZhCode = (code: string): boolean => /^zh\b/i.test(code);

export function detectLang(): BroadcastLang {
  // 1. Telegram UI language
  try {
    const code = selectLanguageCode(getGlobal());
    if (code && isZhCode(code)) return 'zh';
    return 'en';
  } catch {
    // getGlobal may throw in non-context; fall through
  }

  // 2. Browser language
  if (typeof navigator !== 'undefined' && navigator.language) {
    if (isZhCode(navigator.language)) return 'zh';
  }

  // 3. Fallback
  return 'en';
}

// ----- runtime subscription (so initUI can repaint when the lang changes) -----

let currentLang: BroadcastLang = detectLang();
const listeners = new Set<() => void>();

export function getLang(): BroadcastLang {
  return currentLang;
}

export function setLang(lang: BroadcastLang): void {
  if (lang === currentLang) return;
  currentLang = lang;
  listeners.forEach((cb) => cb());
}

export function subscribeLang(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
